/**
 * useLogRotation Hook
 *
 * React hook for managing log rotation
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@xanectech/supabase-client';
import { useToast } from '@xanectech/ui/lib';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('Data - useLogRotation');

interface RotateLogsParams {
  daysOld?: number;
  archiveBeforeDelete?: boolean;
}

interface LogRotationResult {
  success: boolean;
  message: string;
  archived: number;
  deleted: number;
  total_processed: number;
  errors?: string[];
}

function isLogRotationResult(value: unknown): value is LogRotationResult {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<LogRotationResult>;
  return (
    typeof candidate.success === 'boolean' &&
    typeof candidate.message === 'string' &&
    typeof candidate.archived === 'number' &&
    typeof candidate.deleted === 'number' &&
    typeof candidate.total_processed === 'number'
  );
}

/**
 * Fetch user's log metadata
 */
export const useUserLogs = () => {
  return useQuery({
    queryKey: ['user-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('log_metadata')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
};

/**
 * Trigger log rotation
 */
export const useRotateLogs = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: RotateLogsParams = {}) => {
      const invokeResult = await supabase.functions.invoke('rotate-logs', {
        body: params,
      });

      if (invokeResult.error) throw invokeResult.error;
      if (!isLogRotationResult(invokeResult.data)) {
        throw new Error('Invalid rotate-logs response payload');
      }

      return invokeResult.data;
    },
    onSuccess: (data) => {
      toast({
        title: 'Log Rotation Complete',
        description: `Archived: ${data.archived}, Deleted: ${data.deleted}`,
      });
    },
    onError: (error: Error) => {
      logger.error('Log rotation failed', { error: error }, 'log-rotation');

      toast({
        title: 'Log Rotation Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Download log file
 */
export const useDownloadLog = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (logPath: string) => {
      const { data, error } = await supabase.storage
        .from('logs')
        .download(logPath);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = logPath.split('/').pop() || 'log.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Download Started',
        description: 'Your log file is downloading',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Download Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Delete log file and metadata
 */
export const useDeleteLog = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      logId,
      logPath,
    }: {
      logId: string;
      logPath: string;
    }) => {
      // Delete file from storage
      const { error: storageError } = await supabase.storage
        .from('logs')
        .remove([logPath]);

      if (storageError) throw storageError;

      // Delete metadata
      const { error: metaError } = await supabase
        .from('log_metadata')
        .delete()
        .eq('id', logId);

      if (metaError) throw metaError;
    },
    onSuccess: () => {
      toast({
        title: 'Log Deleted',
        description: 'Log file and metadata removed',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Delete Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Get log storage statistics
 */
export const useLogStats = () => {
  return useQuery({
    queryKey: ['log-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('log_metadata')
        .select('file_size, entry_count, log_type');

      if (error) throw error;

      const stats = {
        totalLogs: data.length,
        totalSize: data.reduce((sum, log) => sum + (log.file_size || 0), 0),
        totalEntries: data.reduce(
          (sum, log) => sum + (log.entry_count || 0),
          0,
        ),
        byType: data.reduce(
          (acc, log) => {
            acc[log.log_type] = (acc[log.log_type] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>,
        ),
      };

      return stats;
    },
  });
};
